import React, { useState, useEffect, useRef } from 'react';

/**
 * Lottery-style animated currency display.
 * Counts up from 0 (or previous value) to the target value with easeOutQuart easing.
 * Formats as $X.XM or $XK.
 */
export default function AnimatedCurrency({ value, duration = 1500, className = '' }) {
  const [displayValue, setDisplayValue] = useState(0);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(0);

  useEffect(() => {
    startValueRef.current = displayValue;
    startTimeRef.current = null;

    const animate = (currentTime) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing: smooth deceleration (easeOutQuart)
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      const currentValue = startValueRef.current + (value - startValueRef.current) * easeOutQuart;
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '$0';
    const abs = Math.abs(val);
    if (abs >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${Math.round(val / 1_000).toLocaleString()}K`;
    return `$${Math.round(val).toLocaleString()}`;
  };

  return <span className={className}>${formatCurrency(displayValue).replace('$', '')}</span>;
}
